import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Home, Shield, Car, Heart, Users, Phone, Mail, MapPin, ChevronRight, CheckCircle2, Star, Menu, X, ArrowRight } from "lucide-react";

// Import Pages
import RentersPage from "./components/pages/RentersPage";
import HomeownersPage from "./components/pages/HomeownersPage";
import CarPage from "./components/pages/CarPage";
import LifePage from "./components/pages/LifePage";
import QuotePage from "./components/pages/QuotePage";
import LoginPage from "./components/pages/LoginPage";
import SignupPage from "./components/pages/SignupPage";
import AboutPage from "./components/pages/AboutPage";
import AdminDashboard from "./components/pages/AdminDashboard";
import TermsPage from "./components/pages/TermsPage";
import PrivacyPage from "./components/pages/PrivacyPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component
const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { name: "Renters", href: "/renters" },
    { name: "Homeowners", href: "/homeowners" },
    { name: "Car", href: "/car" },
    { name: "Life", href: "/life" },
    { name: "About", href: "/about" },
  ];

  // Elegant Custom Logo Component
  const Logo = ({ size = "default", light = false }) => (
    <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
      <div className={`${size === "large" ? "w-12 h-12" : "w-10 h-10"} ${light ? "bg-white/20" : "bg-gradient-to-br from-navy-600 to-navy-800"} rounded-xl flex items-center justify-center shadow-lg`}>
        <span className={`${size === "large" ? "text-2xl" : "text-xl"} font-bold text-white`} style={{ fontFamily: "'Playfair Display', serif" }}>M</span>
      </div>
      <div className="flex flex-col -space-y-1">
        <span 
          className={`${size === "large" ? "text-2xl" : "text-xl"} font-semibold tracking-wide ${light ? "text-white" : "text-navy-900"}`}
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          My Insurance
        </span>
        <span 
          className={`${size === "large" ? "text-base" : "text-sm"} font-medium tracking-widest uppercase ${light ? "text-slate-300" : "text-slate-500"}`}
          style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}
        >
          Coverage
        </span>
      </div>
    </Link>
  );

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Logo />

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm font-medium transition-colors hover:text-navy-600 ${
                  location.pathname === item.href ? "text-navy-600" : "text-slate-600"
                }`}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            <Link
              to="/login"
              className="text-sm font-medium text-slate-600 hover:text-navy-600 transition-colors"
              data-testid="nav-login"
            >
              Sign In
            </Link>
            <Link
              to="/quote"
              className="px-6 py-2.5 bg-gradient-to-r from-navy-600 to-navy-700 text-white text-sm font-semibold rounded-full hover:from-navy-700 hover:to-navy-800 transition-all shadow-lg shadow-navy-600/25 hover:shadow-navy-600/40"
              data-testid="nav-get-quote"
            >
              Get a Quote
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-slate-600"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-slate-100" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-slate-600 hover:text-navy-600 font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <div className="flex flex-col gap-3 pt-4 border-t border-slate-100">
                <Link to="/login" className="text-slate-600 font-medium">Sign In</Link>
                <Link
                  to="/quote"
                  className="px-6 py-2.5 bg-gradient-to-r from-navy-600 to-navy-700 text-white text-sm font-semibold rounded-full text-center"
                >
                  Get a Quote
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="pt-32 pb-20 bg-gradient-to-b from-slate-50 to-white" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 rounded-full text-sm font-medium mb-6">
              <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
              Rated #1 Insurance Provider 2025
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy-900 leading-tight mb-6">
              Insurance Made
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-navy-600 to-blue-600">
                Simple & Secure
              </span>
            </h1>
            <p className="text-lg text-slate-600 mb-8 max-w-xl mx-auto lg:mx-0">
              Protect what matters most with coverage you can trust. Get personalized quotes in minutes, not hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Link
                to="/quote"
                className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-navy-600 to-navy-700 text-white font-semibold rounded-full hover:from-navy-700 hover:to-navy-800 transition-all shadow-xl shadow-navy-600/30 hover:shadow-navy-600/50"
                data-testid="hero-get-quote"
              >
                Get Your Free Quote
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-navy-700 font-semibold rounded-full border-2 border-slate-200 hover:border-navy-200 hover:bg-slate-50 transition-all"
                data-testid="hero-learn-more"
              >
                Learn More
              </Link>
            </div>
            {/* Trust Badges */}
            <div className="flex items-center justify-center lg:justify-start gap-8 mt-10 pt-10 border-t border-slate-200">
              <div className="text-center">
                <p className="text-3xl font-bold text-navy-900">500K+</p>
                <p className="text-sm text-slate-500">Happy Customers</p>
              </div>
              <div className="w-px h-12 bg-slate-200"></div>
              <div className="text-center">
                <p className="text-3xl font-bold text-navy-900">4.9/5</p>
                <p className="text-sm text-slate-500">Customer Rating</p>
              </div>
              <div className="w-px h-12 bg-slate-200"></div>
              <div className="text-center">
                <p className="text-3xl font-bold text-navy-900">24/7</p>
                <p className="text-sm text-slate-500">Support</p>
              </div>
            </div>
          </div>

          {/* Right Visual */}
          <div className="relative hidden lg:block">
            <div className="absolute inset-0 bg-gradient-to-br from-navy-100 to-blue-100 rounded-3xl transform rotate-3"></div>
            <div className="relative bg-white rounded-3xl shadow-2xl p-8 border border-slate-100">
              <div className="space-y-6">
                <Link to="/homeowners" className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl hover:bg-navy-50 hover:shadow-md transition-all cursor-pointer">
                  <div className="w-14 h-14 bg-gradient-to-br from-navy-500 to-navy-700 rounded-xl flex items-center justify-center">
                    <Home className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-navy-900">Home Insurance</p>
                    <p className="text-sm text-slate-500">From $25/month</p>
                  </div>
                  <CheckCircle2 className="w-6 h-6 text-emerald-500 ml-auto" />
                </Link>
                <Link to="/car" className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl hover:bg-blue-50 hover:shadow-md transition-all cursor-pointer">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center">
                    <Car className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-navy-900">Auto Insurance</p>
                    <p className="text-sm text-slate-500">From $30/month</p>
                  </div>
                  <CheckCircle2 className="w-6 h-6 text-emerald-500 ml-auto" />
                </Link>
                <Link to="/life" className="flex items-center gap-4 p-4 bg-amber-50 rounded-2xl border-2 border-amber-200 hover:bg-amber-100 hover:shadow-md transition-all cursor-pointer">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center">
                    <Heart className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-navy-900">Life Insurance</p>
                    <p className="text-sm text-slate-500">From $8/month</p>
                  </div>
                  <span className="ml-auto px-3 py-1 bg-amber-200 text-amber-800 text-xs font-semibold rounded-full">Popular</span>
                </Link>
                <Link to="/renters" className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl hover:bg-blue-50 hover:shadow-md transition-all cursor-pointer">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center">
                    <Shield className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-navy-900">Renters Insurance</p>
                    <p className="text-sm text-slate-500">From $5/month</p>
                  </div>
                  <CheckCircle2 className="w-6 h-6 text-emerald-500 ml-auto" />
                </Link>
              </div>
              <div className="mt-6 p-4 bg-gradient-to-r from-navy-600 to-navy-700 rounded-2xl text-white">
                <p className="text-sm opacity-90">Total Monthly Estimate</p>
                <p className="text-3xl font-bold">$68<span className="text-lg font-normal opacity-75">/mo</span></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Insurance Products Section
const InsuranceProducts = () => {
  const products = [
    {
      icon: Home,
      title: "Renters Insurance",
      description: "Coverage for your belongings, liability protection, and additional living expenses if you need to relocate temporarily.",
      price: "$5",
      features: ["Personal property coverage", "Liability protection", "Additional living expenses"],
      href: "/renters",
      gradient: "from-blue-500 to-blue-600",
    },
    {
      icon: Shield,
      title: "Homeowners Insurance",
      description: "Comprehensive protection for your home, belongings, and liability coverage for peace of mind.",
      price: "$25",
      features: ["Dwelling coverage", "Personal property", "Liability coverage"],
      href: "/homeowners",
      gradient: "from-navy-500 to-navy-700",
    },
    {
      icon: Car,
      title: "Auto Insurance",
      description: "Protect your vehicle and passengers with comprehensive coverage options tailored to your needs.",
      price: "$30",
      features: ["Collision coverage", "Comprehensive coverage", "Roadside assistance"],
      href: "/car",
      gradient: "from-slate-600 to-slate-800",
    },
    {
      icon: Heart,
      title: "Term Life Insurance",
      description: "Secure your family's financial future with affordable term life coverage.",
      price: "$8",
      features: ["Death benefit", "Fixed premiums", "Convertible options"],
      href: "/life",
      gradient: "from-amber-500 to-amber-600",
    },
  ];

  return (
    <section className="py-24 bg-white" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-navy-50 text-navy-600 rounded-full text-sm font-semibold mb-4">
            Our Coverage Options
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-4">
            Affordable Protection for
            <span className="block">What Matters Most</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Choose from our range of insurance products designed to give you peace of mind at competitive rates.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((product, index) => (
            <div
              key={index}
              className="group bg-white rounded-2xl border border-slate-200 hover:border-navy-200 hover:shadow-xl hover:shadow-navy-100/50 transition-all duration-300 overflow-hidden"
              data-testid={`product-card-${product.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="p-6">
                <div className={`w-14 h-14 bg-gradient-to-br ${product.gradient} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <product.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-2">{product.title}</h3>
                <p className="text-slate-600 text-sm mb-4 line-clamp-2">{product.description}</p>
                <ul className="space-y-2 mb-6">
                  {product.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-sm text-slate-500">From</span>
                  <span className="text-3xl font-bold text-navy-900">{product.price}</span>
                  <span className="text-slate-500">/mo</span>
                </div>
              </div>
              <Link
                to={product.href}
                className="flex items-center justify-center gap-2 p-4 bg-slate-50 group-hover:bg-navy-600 text-navy-600 group-hover:text-white font-semibold transition-colors"
              >
                Get a Quote
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUs = () => {
  const features = [
    {
      icon: "⚡",
      title: "Fast & Easy",
      description: "Get quotes in minutes with our streamlined online application. No lengthy paperwork or waiting.",
    },
    {
      icon: "💰",
      title: "Competitive Rates",
      description: "We shop multiple top-rated carriers to find you the best coverage at the best price.",
    },
    {
      icon: "🛡️",
      title: "Trusted Coverage",
      description: "Work with licensed professionals backed by A-rated insurance carriers you can trust.",
    },
    {
      icon: "📱",
      title: "24/7 Support",
      description: "Our dedicated team is available around the clock to help with claims and questions.",
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-slate-50 to-white" data-testid="why-choose-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="inline-block px-4 py-2 bg-amber-50 text-amber-700 rounded-full text-sm font-semibold mb-4">
              Why Brad Greenberg?
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-6">
              Insurance You Can
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-navy-600 to-blue-600"> Count On</span>
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              For over 15 years, we've been helping families and individuals protect what matters most. Our commitment to exceptional service and competitive rates has earned us the trust of over 500,000 customers.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="flex gap-4">
                  <div className="w-12 h-12 bg-white rounded-xl shadow-md flex items-center justify-center text-2xl flex-shrink-0">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy-900 mb-1">{feature.title}</h3>
                    <p className="text-sm text-slate-600">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-navy-100 to-amber-100 rounded-3xl transform -rotate-2"></div>
            <div className="relative bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
              <div className="flex items-center gap-4 mb-6">
                <div className="flex -space-x-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className={`w-10 h-10 rounded-full border-2 border-white bg-gradient-to-br ${
                      i === 1 ? 'from-navy-400 to-navy-600' :
                      i === 2 ? 'from-blue-400 to-blue-600' :
                      i === 3 ? 'from-amber-400 to-amber-600' :
                      'from-slate-400 to-slate-600'
                    }`}></div>
                  ))}
                </div>
                <div>
                  <p className="font-semibold text-navy-900">500,000+ customers</p>
                  <p className="text-sm text-slate-500">trust Brad Greenberg</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 text-sm italic">"Working with Brad saved me over $500 a year. The process was incredibly simple!"</p>
                  <p className="text-slate-500 text-sm mt-2">— Sarah M., Austin TX</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 text-sm italic">"Best customer service I've ever experienced with an insurance company. Highly recommend!"</p>
                  <p className="text-slate-500 text-sm mt-2">— Michael R., Denver CO</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="py-24 bg-gradient-to-br from-navy-800 via-navy-900 to-slate-900" data-testid="cta-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
          Ready to Protect What Matters?
        </h2>
        <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
          Join hundreds of satisfied customers who trust Brad Greenberg for their insurance needs. Get your personalized quote in just minutes.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/quote"
            className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-navy-900 font-bold rounded-full hover:from-amber-400 hover:to-amber-500 transition-all shadow-xl shadow-amber-500/30"
            data-testid="cta-get-quote"
          >
            Get Your Free Quote
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-semibold rounded-full hover:bg-white/20 transition-all border border-white/20"
            data-testid="cta-contact"
          >
            <Phone className="w-5 h-5" />
            Call 201.227.6413
          </Link>
        </div>
        <p className="text-slate-400 text-sm mt-6">
          No commitment required • Free quotes • Compare rates instantly
        </p>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-slate-900 text-slate-300" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                <span className="text-xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>M</span>
              </div>
              <div className="flex flex-col -space-y-1">
                <span className="text-xl font-semibold text-white tracking-wide" style={{ fontFamily: "'Playfair Display', serif" }}>
                  My Insurance
                </span>
                <span className="text-xs font-medium text-slate-400 tracking-widest uppercase" style={{ letterSpacing: '0.15em' }}>
                  Coverage
                </span>
              </div>
            </div>
            <p className="text-slate-400 mb-4">
              Your trusted insurance partner. Protecting what matters most.
            </p>
            <p className="text-slate-500 text-sm mb-4">Licensed Insurance Agent</p>
            <div className="flex gap-4">
              <a href="https://www.linkedin.com/in/ashtonbenefits" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors">
                <span className="sr-only">LinkedIn</span>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
              </a>
              <a href="mailto:Bgreenberg@acrisure.com" className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-amber-600 transition-colors">
                <span className="sr-only">Email</span>
                <Mail className="w-5 h-5" />
              </a>
              <a href="tel:201-227-6413" className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-emerald-600 transition-colors">
                <span className="sr-only">Phone</span>
                <Phone className="w-5 h-5" />
              </a>
            </div>
          </div>
          <div>
            <h3 className="text-white font-semibold mb-4">Products</h3>
            <ul className="space-y-3">
              <li><Link to="/renters" className="hover:text-white transition-colors">Renters Insurance</Link></li>
              <li><Link to="/homeowners" className="hover:text-white transition-colors">Homeowners Insurance</Link></li>
              <li><Link to="/car" className="hover:text-white transition-colors">Auto Insurance</Link></li>
              <li><Link to="/life" className="hover:text-white transition-colors">Life Insurance</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="text-white font-semibold mb-4">Company</h3>
            <ul className="space-y-3">
              <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
              <li><Link to="/careers" className="hover:text-white transition-colors">Careers</Link></li>
              <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
              <li><Link to="/press" className="hover:text-white transition-colors">Press</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="text-white font-semibold mb-4">Contact</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                <a href="tel:201-227-6413" className="hover:text-white transition-colors">201.227.6413</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                <a href="mailto:Bgreenberg@acrisure.com" className="hover:text-white transition-colors">Bgreenberg@acrisure.com</a>
              </li>
              <li className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                <a href="https://www.linkedin.com/in/ashtonbenefits" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">LinkedIn</a>
              </li>
            </ul>
            <div className="mt-4 pt-4 border-t border-slate-700">
              <p className="text-slate-400 text-sm">Bradley Greenberg</p>
              <p className="text-slate-500 text-xs">Licensed Insurance Agent</p>
            </div>
          </div>
        </div>
        <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © 2025 Brad Greenberg Insurance Services. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/licenses" className="hover:text-white transition-colors">Licenses</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Home Page
const HomePage = () => {
  return (
    <div>
      <Navigation />
      <HeroSection />
      <InsuranceProducts />
      <WhyChooseUs />
      <CTASection />
      <Footer />
    </div>
  );
};

// Placeholder pages for routes
const PlaceholderPage = ({ title }) => (
  <div>
    <Navigation />
    <div className="pt-32 pb-20 min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 text-center">
        <h1 className="text-4xl font-bold text-navy-900 mb-4">{title}</h1>
        <p className="text-slate-600 mb-8">This page is coming soon. Please check back later.</p>
        <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-navy-600 text-white rounded-full font-semibold hover:bg-navy-700 transition-colors">
          Back to Home
        </Link>
      </div>
    </div>
    <Footer />
  </div>
);

// Wrapper for pages that need Nav + Footer
const PageWrapper = ({ children }) => (
  <div>
    <Navigation />
    {children}
    <Footer />
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/renters" element={<PageWrapper><RentersPage /></PageWrapper>} />
          <Route path="/homeowners" element={<PageWrapper><HomeownersPage /></PageWrapper>} />
          <Route path="/car" element={<PageWrapper><CarPage /></PageWrapper>} />
          <Route path="/life" element={<><Navigation /><LifePage /></>} />
          <Route path="/about" element={<PageWrapper><AboutPage /></PageWrapper>} />
          <Route path="/quote" element={<><Navigation /><QuotePage /></>} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/contact" element={<PlaceholderPage title="Contact Us" />} />
          <Route path="/privacy" element={<><Navigation /><PrivacyPage /><Footer /></>} />
          <Route path="/terms" element={<><Navigation /><TermsPage /><Footer /></>} />
          <Route path="/licenses" element={<PlaceholderPage title="Licenses" />} />
          <Route path="/careers" element={<PlaceholderPage title="Careers" />} />
          <Route path="/blog" element={<PlaceholderPage title="Blog" />} />
          <Route path="/press" element={<PlaceholderPage title="Press" />} />
          <Route path="/forgot-password" element={<PlaceholderPage title="Reset Password" />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
